import { Spin } from 'antd';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import {
  REACT_APP_HOSPITAL_CODE,
  USER_ACTIVITY_EVENT,
  USER_ACTIVITY_TYPE
} from 'src/enums';
import { ApiService } from 'src/services';
import IdleTimer from 'src/services/logoutTimer/IdleTimer';
import {
  documentsActions,
  useDispatch,
  userActions,
  userSelectors,
  useSelector
} from 'src/store';
import { logoutTimeoutModalActions } from 'src/store/reducers/logoutTimeoutModal/logoutTimeoutModal.reducer';
import { IUser } from 'src/types/users.types';
import { createLog, getErrorMessage, logOut } from 'src/utils';

import classes from './style/auth.module.css';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IPrivatePageProps {
  children: any;
}

export const PrivatePage: React.FunctionComponent<IPrivatePageProps> = (
  props
) => {
  // TOOLS
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // STATE
  const [authState, setAuthState] = React.useState<{
    loader: boolean;
    error: null | string;
  }>({ loader: false, error: null });
  const [mounted, setMount] = React.useState(false);

  // SELECTORS
  const user = useSelector(userSelectors.selectUser);
  const hospital = useSelector(userSelectors.selectUserHospital);

  const timerRef = React.useRef<IdleTimer>();

  React.useEffect(() => {
    getCurrentSession();
  }, []);

  const handleClearLogoutTimer = () => {
    timerRef.current?.cleanUp();
  };

  const handleLogoutTimer = () => {
    if (!timerRef.current) {
      timerRef.current = new IdleTimer({
        onTimeout: async () => {
          await signOut(); // delete the cognito token from the local storage, so if the user refresh the screen after pop up, it will go back to the login screen
          dispatch(logoutTimeoutModalActions.openModal());
          // here there is no logout because we want to show the user that he was connected before, so we show him a modal
        },
        onExpired: async () => {
          await signOut(); // delete the cognito token from the local storage, so if the user refresh the screen after pop up, it will go back to the login screen
          logOut();
        }
      });
    }
  };

  const getCurrentSession = async () => {
    try {
      setAuthState({ loader: true, error: null });
      const authSession = await fetchAuthSession();
      const { idToken } = authSession?.tokens || {};
      console.log('private');
      if (idToken) {
        ApiService.setAuthorization(idToken);
      }

      if (!REACT_APP_HOSPITAL_CODE) {
        console.log('error in private page: HOSPITAL_CODE_NOT_FOUND');
        return;
      }
      // ************************* refreshing: get user from tibco *************************
      let refreshed_user = user;
      if (!user) {
        const res = await ApiService.getPatientDetailes({
          HospitalCode: REACT_APP_HOSPITAL_CODE,
          DeviceId: 'deviceId',
          Token: 'deviceToken',
          IsPatient: true
        });

        const { data } = res;
        if (data.group?.error) {
          console.log('error in getPatientDetailes==>1', data);
          throw new Error(data.group?.error);
        }
        if (data.ErrorCode) {
          console.log('error in getPatientDetailes==>2', data);
          throw new Error(data.ErrorCode);
        }
        refreshed_user = res.data as IUser;
      }

      if (refreshed_user) {
        //||| get data |||///

        // ******** fetch documents
        dispatch(
          documentsActions.onFetchDocumentsList({
            isPatient: true,
            unitCode: refreshed_user.MedicalUnit
          }) as any
        );
        // get hospital
        const hospitalResult = await ApiService.fetchHospital({
          hospitalCode: REACT_APP_HOSPITAL_CODE
        });

        const hospital = hospitalResult.data;
        if (!hospital) {
          throw new Error('No hospital found');
        }

        // set hospital
        dispatch(userActions.setUserHospital({ hospital }) as any);

        // maybe in the future the accompanes will be able to login as well
        dispatch(
          userActions.setIsPatient({
            value: true
          })
        );

        dispatch(
          userActions.setUser({
            value: refreshed_user
          })
        );
      }

      handleLogoutTimer();
      // create a "view screen" activity log
      createLog(
        refreshed_user,
        USER_ACTIVITY_EVENT.VIEW,
        USER_ACTIVITY_TYPE.SCREEN,
        location.pathname
      );

      setMount(true);
      setAuthState({ loader: false, error: null });
    } catch (error) {
      handleClearLogoutTimer();
      setMount(true);
      setAuthState({ loader: false, error: getErrorMessage(error) });
    }
  };

  if (authState.loader) {
    return (
      <div className={classes.loadingPage}>
        <img
          width={150}
          src={hospital?.logo || '/assets/space_saver.png'}
          alt={t('accessibility.ministryOfHealthLogo')}
          title={t('accessibility.ministryOfHealthLogo')}
          style={{ backgroundColor: 'white' }}
        />
        <Spin />
      </div>
    );
  }
  if (mounted) {
    if (user) {
      return props.children;
    } else {
      return <Navigate to={`/login`} />;
    }
  }
};
